import {
  PERSONAS,
  agent as catalogAgent,
  persona as catalogPersona,
  realize,
} from '@vagusx/shared';
import type { CampaignSpec } from '@vagusx/shared';
import type { AudienceCatalogEntry, ContactPolicy } from '@vagusx/clients';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { CampaignSettings } from './CampaignSettings';
import { Field } from './Field';
import { PreviewColumn } from './PreviewColumn';
import type { StepContext } from './StepCard';
import { Timeline } from './Timeline';

/** The campaign row's own fields, which the settings band also edits. */
export interface CampaignDetails {
  name: string;
  description: string;
  priority: number;
  endWhenNoLongerQualifies: boolean;
}

/**
 * Steps (ADR-318, P11): what happens, and when. The campaign-wide rules first
 * (the voice, calling days and hours, limits), then each step, then the rules
 * that hold on every step whatever is configured, and beside it all what a
 * patient who never picks up would get.
 */
export function StepsStep({
  spec,
  onSpecChange,
  details,
  onDetailsChange,
  policy,
  catalogEntry,
  context,
  openStepId,
  onOpenStepChange,
  openRuleId,
  onOpenRuleChange,
  active,
}: {
  spec: CampaignSpec;
  onSpecChange: (spec: CampaignSpec) => void;
  details: CampaignDetails;
  onDetailsChange: (details: CampaignDetails) => void;
  policy: ContactPolicy | undefined;
  catalogEntry: AudienceCatalogEntry | undefined;
  context: StepContext;
  openStepId: string;
  onOpenStepChange: (id: string) => void;
  openRuleId: string;
  onOpenRuleChange: (id: string) => void;
  /** The Steps tab is showing; the calendar simulates only then. */
  active: boolean;
}): React.ReactElement {
  return (
    <div className="grid gap-6 xl:grid-cols-3">
      <div className="min-w-0 space-y-8 xl:col-span-2">
        <section className="space-y-2">
          <h3 className="text-sm font-medium">For the whole campaign</h3>
          <div className="rounded-lg border px-4">
            <VoiceField spec={spec} onSpecChange={onSpecChange} />
          </div>
          <CampaignSettings
            spec={spec}
            onSpecChange={onSpecChange}
            description={details.description}
            onDescriptionChange={(description) => onDetailsChange({ ...details, description })}
            priority={details.priority}
            onPriorityChange={(priority) => onDetailsChange({ ...details, priority })}
            endWhenNoLongerQualifies={details.endWhenNoLongerQualifies}
            onEndWhenNoLongerQualifiesChange={(endWhenNoLongerQualifies) =>
              onDetailsChange({ ...details, endWhenNoLongerQualifies })
            }
            policy={policy}
            catalogEntry={catalogEntry}
            open={openRuleId}
            onOpenChange={onOpenRuleChange}
          />
        </section>

        <section className="space-y-2">
          <h3 className="text-sm font-medium">Steps</h3>
          <p className="text-xs text-muted-foreground">
            Each step is one way of reaching them: the workflow it runs, when, and what to do if
            nobody picks up.
          </p>
          <Timeline
            spec={spec}
            onSpecChange={onSpecChange}
            openId={openStepId}
            onOpenChange={onOpenStepChange}
            context={context}
          />
        </section>

        <AlwaysRules spec={spec} />
      </div>

      <div className="h-fit xl:sticky xl:top-4">
        <PreviewColumn
          spec={spec}
          fixedOutcome="no_answer"
          title="If they never pick up"
          variant="card"
          enabled={active}
        />
      </div>
    </div>
  );
}

/**
 * The campaign's voice (P4): one persona speaks on every call of the sequence,
 * whatever stack carries each step. A step on a stack that cannot speak as the
 * new voice goes back to the default stack rather than staying unpublishable.
 */
function VoiceField({
  spec,
  onSpecChange,
}: {
  spec: CampaignSpec;
  onSpecChange: (spec: CampaignSpec) => void;
}): React.ReactElement {
  return (
    <Field
      label="Voice"
      htmlFor="campaign-voice"
      hint="Every call in this campaign uses this voice."
    >
      <Select
        value={spec.personaId}
        onValueChange={(personaId) => {
          const voice = catalogPersona(personaId);
          onSpecChange({
            ...spec,
            personaId,
            sequence: spec.sequence.map((step) => {
              const stack = step.agentId ? catalogAgent(step.agentId) : null;
              return voice && stack && !realize(voice, stack).ok
                ? { ...step, agentId: null }
                : step;
            }),
          });
        }}
      >
        <SelectTrigger id="campaign-voice" className="w-56">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {PERSONAS.map((p) => (
            <SelectItem key={p.id} value={p.id}>
              {p.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </Field>
  );
}

/**
 * What holds on every step, whatever the steps say. Statements, not settings —
 * but read from the spec where the spec decides them (`stopOn`), so a row never
 * promises something the campaign was changed not to do.
 */
export function AlwaysRules({ spec }: { spec: CampaignSpec }): React.ReactElement {
  const stopsOn = (outcome: string): boolean => spec.stopOn.includes(outcome);
  // Only what the campaign engine guarantees. What Iris says on a call is the
  // workflow's to decide, so it is not promised here.
  const rows: Array<{ title: string; text: string }> = [
    ...(stopsOn('wrong_number')
      ? [
          {
            title: 'Wrong number',
            text: 'The campaign stops for them, and the referral goes back to the front desk.',
          },
        ]
      : []),
    ...(stopsOn('declined')
      ? [{ title: 'They say no', text: 'The campaign stops for them.' }]
      : []),
    {
      title: 'They ask to be called back',
      text: 'On a step that honours a requested time, the next call is at the time they asked. The schedule pauses until then, and a missed callback does not use up a try.',
    },
    {
      title: 'They ask us to stop',
      text: 'No campaign calls them again.',
    },
  ];

  return (
    <section className="space-y-2">
      <div className="flex items-baseline justify-between gap-4">
        <h3 className="text-sm font-medium">On every step, always</h3>
        <span className="text-xs text-muted-foreground">rules, not settings</span>
      </div>
      <div className="divide-y rounded-lg border">
        {rows.map((row) => (
          <div key={row.title} className="flex items-start justify-between gap-4 px-4 py-3">
            <div className="min-w-0">
              <div className="text-sm font-medium">{row.title}</div>
              <p className="text-xs text-muted-foreground">{row.text}</p>
            </div>
            <Badge variant="secondary">Always</Badge>
          </div>
        ))}
      </div>
    </section>
  );
}
